import { useState, useEffect, useRef } from 'react';
import { api, SiteStatus, SiteIntakeData, InfluencerSiteAsset, SiteAssetType } from '../api';

interface SocialLinks {
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  twitter?: string;
  other?: string;
}

const MUST_HAVE_PAGES = [
  { key: 'home', label: 'Home' },
  { key: 'shop', label: 'Shop' },
  { key: 'about', label: 'About' },
  { key: 'faq', label: 'FAQ' },
  { key: 'contact', label: 'Contact' },
  { key: 'blog', label: 'Blog' },
];

const ASSET_TYPE_LABELS: Record<SiteAssetType, string> = {
  logo: 'Logo',
  brand_photos: 'Brand Photos',
  product_photos: 'Product Photos',
  brand_kit: 'Brand Kit',
  other: 'Other',
};

function ProgressWidget({ status }: { status: SiteStatus }) {
  const steps = [
    { key: 'intake', label: 'Intake Received', completed: status !== 'form_incomplete' },
    { key: 'design', label: 'Design In Progress', completed: status === 'website_in_progress' || status === 'website_completed' },
    { key: 'build', label: 'Build In Progress', completed: status === 'website_in_progress' || status === 'website_completed' },
    { key: 'complete', label: 'Site Complete', completed: status === 'website_completed' },
  ];

  const progress = status === 'form_incomplete' ? 0 
    : status === 'form_completed' ? 25 
    : status === 'website_in_progress' ? 75 
    : 100;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-ink-100">Website Progress</h3>
        <span className="text-accent-400 font-semibold">{progress}%</span>
      </div>
      <div className="h-2 bg-ink-800 rounded-full overflow-hidden mb-6">
        <div 
          className="h-full bg-gradient-to-r from-accent-500 to-royal-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="space-y-3">
        {steps.map((step, idx) => (
          <div key={step.key} className="flex items-center gap-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              step.completed 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'bg-ink-800 text-ink-500'
            }`}>
              {step.completed ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-xs">{idx + 1}</span>
              )}
            </div>
            <span className={step.completed ? 'text-ink-100' : 'text-ink-500'}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InfluencerYourSite() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Site data
  const [status, setStatus] = useState<SiteStatus>('form_incomplete');
  const [siteDomain, setSiteDomain] = useState<string | null>(null);
  const [assets, setAssets] = useState<InfluencerSiteAsset[]>([]);
  
  // Form state
  const [brandVibe, setBrandVibe] = useState('');
  const [colorScheme, setColorScheme] = useState('');
  const [fontPreferences, setFontPreferences] = useState('');
  const [inspirationUrls, setInspirationUrls] = useState<string[]>(['']);
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({});
  const [aboutCopy, setAboutCopy] = useState('');
  const [callouts, setCallouts] = useState('');
  const [mustHavePages, setMustHavePages] = useState<string[]>(['home', 'shop']);
  const [notes, setNotes] = useState('');
  
  // Upload state
  const [uploadingType, setUploadingType] = useState<SiteAssetType | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAssetType, setSelectedAssetType] = useState<SiteAssetType>('logo');

  useEffect(() => {
    loadSiteData();
  }, []);

  const loadSiteData = async () => {
    try {
      const data = await api.getMySite();
      setStatus(data.status);
      setSiteDomain(data.site_domain);
      setAssets(data.assets);
      
      // Populate form with existing data
      if (data.intake) {
        setBrandVibe(data.intake.brand_vibe || '');
        setColorScheme(data.intake.color_scheme || '');
        setFontPreferences(data.intake.font_preferences || '');
        setAboutCopy(data.intake.about_copy || '');
        setCallouts(data.intake.callouts || '');
        setNotes(data.intake.notes || '');
        
        if (data.intake.inspiration_urls) {
          const urls = data.intake.inspiration_urls.split(',').filter(Boolean);
          setInspirationUrls(urls.length > 0 ? urls : ['']);
        }
        
        if (data.intake.social_links) {
          try {
            setSocialLinks(JSON.parse(data.intake.social_links));
          } catch {}
        }
        
        if (data.intake.must_have_pages) {
          try {
            setMustHavePages(JSON.parse(data.intake.must_have_pages));
          } catch {}
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load site data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIntake = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const data: SiteIntakeData = {
        brand_vibe: brandVibe,
        color_scheme: colorScheme,
        font_preferences: fontPreferences,
        inspiration_urls: inspirationUrls.filter(Boolean).join(','),
        social_links: JSON.stringify(socialLinks),
        about_copy: aboutCopy,
        callouts: callouts,
        must_have_pages: JSON.stringify(mustHavePages),
        notes: notes,
      };

      const result = await api.submitSiteIntake(data);
      setStatus(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit intake form');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingType(selectedAssetType);
    setUploadProgress(0);

    try {
      // Get presigned URL
      const presign = await api.presignSiteAsset({
        assetType: selectedAssetType,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      setUploadProgress(30);

      // Upload to R2
      await api.uploadSiteAsset(presign.uploadUrl, file);
      setUploadProgress(70);

      // Complete upload
      const result = await api.completeSiteAssetUpload({
        assetType: selectedAssetType,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        objectKey: presign.objectKey,
        publicUrl: presign.publicUrl,
      });

      setAssets([result.asset, ...assets]);
      setUploadProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploadingType(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      await api.deleteSiteAsset(assetId);
      setAssets(assets.filter(a => a.id !== assetId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete asset');
    }
  };

  const addInspirationUrl = () => {
    setInspirationUrls([...inspirationUrls, '']);
  };

  const updateInspirationUrl = (index: number, value: string) => {
    const newUrls = [...inspirationUrls];
    newUrls[index] = value;
    setInspirationUrls(newUrls);
  };

  const removeInspirationUrl = (index: number) => {
    setInspirationUrls(inspirationUrls.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
    );
  }

  // Show completion state
  if (status === 'website_completed' && siteDomain) {
    return (
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-display font-bold text-ink-50 mb-2">Your Website is Live!</h1>
            <p className="text-ink-400 mb-6">Congratulations! Your custom website has been built and deployed.</p>
            <a 
              href={siteDomain}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Visit Your Site
            </a>
          </div>
          <ProgressWidget status={status} />
        </div>
    );
  }

  // Show in-progress state
  if (status === 'form_completed' || status === 'website_in_progress') {
    return (
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="text-center card p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-accent-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h1 className="text-2xl font-display font-bold text-ink-50 mb-2">
              Your Website is Being Built!
            </h1>
            <p className="text-ink-400 mb-2">
              We received your intake form and our team is working on your custom website.
            </p>
            <p className="text-ink-500 text-sm">
              Check back soon for updates on your progress.
            </p>
          </div>

          <ProgressWidget status={status} />

          {/* Summary of submitted info */}
          <div className="card p-6">
            <h3 className="font-display font-bold text-ink-100 mb-4">Your Submission</h3>
            <dl className="space-y-3 text-sm">
              {brandVibe && (
                <div>
                  <dt className="text-ink-500">Brand Vibe</dt>
                  <dd className="text-ink-200">{brandVibe}</dd>
                </div>
              )}
              {colorScheme && (
                <div>
                  <dt className="text-ink-500">Colors</dt>
                  <dd className="text-ink-200">{colorScheme}</dd>
                </div>
              )}
              {Object.values(socialLinks).some(Boolean) && (
                <div>
                  <dt className="text-ink-500">Social Links</dt>
                  <dd className="text-ink-200">
                    {Object.entries(socialLinks)
                      .filter(([, v]) => v)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' • ')}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Uploaded assets */}
          {assets.length > 0 && (
            <div className="card p-6">
              <h3 className="font-display font-bold text-ink-100 mb-4">Uploaded Assets</h3>
              <div className="space-y-2">
                {assets.map((asset) => (
                  <div key={asset.id} className="flex items-center gap-3 p-2 bg-ink-800/50 rounded">
                    <div className="w-10 h-10 bg-ink-700 rounded flex items-center justify-center">
                      {asset.content_type.startsWith('image/') ? (
                        <img src={asset.public_url} alt="" className="w-full h-full object-cover rounded" />
                      ) : (
                        <svg className="w-5 h-5 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink-200 truncate">{asset.file_name}</p>
                      <p className="text-xs text-ink-500">
                        {ASSET_TYPE_LABELS[asset.asset_type]} • {formatFileSize(asset.size_bytes)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
    );
  }

  // Show intake form
  return (
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-ink-50">Your Site</h1>
          <p className="text-ink-400 mt-1">
            Tell us about your brand so we can build your perfect website.
          </p>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmitIntake} className="space-y-8">
          {/* Brand & Vibe */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Brand & Vibe</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  Describe your brand vibe *
                </label>
                <textarea
                  className="input w-full"
                  rows={3}
                  value={brandVibe}
                  onChange={(e) => setBrandVibe(e.target.value)}
                  placeholder="e.g., Bold and edgy, minimalist luxury, playful and colorful, rustic and authentic..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  Color scheme *
                </label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={colorScheme}
                  onChange={(e) => setColorScheme(e.target.value)}
                  placeholder="e.g., Black and gold with pops of red, soft pastels, earth tones..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  Font preferences
                </label>
                <input
                  type="text"
                  className="input w-full"
                  value={fontPreferences}
                  onChange={(e) => setFontPreferences(e.target.value)}
                  placeholder="e.g., Modern sans-serif, elegant serif, handwritten feel..."
                />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Social Links *</h2>
            <p className="text-sm text-ink-400 mb-4">Add at least one social link.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Instagram</label>
                <input
                  type="url"
                  className="input w-full"
                  value={socialLinks.instagram || ''}
                  onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                  placeholder="https://instagram.com/yourhandle"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">TikTok</label>
                <input
                  type="url"
                  className="input w-full"
                  value={socialLinks.tiktok || ''}
                  onChange={(e) => setSocialLinks({ ...socialLinks, tiktok: e.target.value })}
                  placeholder="https://tiktok.com/@yourhandle"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">YouTube</label>
                <input
                  type="url"
                  className="input w-full"
                  value={socialLinks.youtube || ''}
                  onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })}
                  placeholder="https://youtube.com/@yourhandle"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">X (Twitter)</label>
                <input
                  type="url"
                  className="input w-full"
                  value={socialLinks.twitter || ''}
                  onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                  placeholder="https://x.com/yourhandle"
                />
              </div>
            </div>
          </div>

          {/* Inspiration */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Inspiration</h2>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">
                Websites you love
              </label>
              <p className="text-sm text-ink-500 mb-3">
                Share links to websites whose style or features you admire.
              </p>
              <div className="space-y-2">
                {inspirationUrls.map((url, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="url"
                      className="input flex-1"
                      value={url}
                      onChange={(e) => updateInspirationUrl(idx, e.target.value)}
                      placeholder="https://example.com"
                    />
                    {inspirationUrls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInspirationUrl(idx)}
                        className="btn-ghost p-2 text-ink-400 hover:text-accent-400"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addInspirationUrl}
                className="btn-ghost text-sm mt-2"
              >
                + Add another
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Content & Messaging</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  About / Bio
                </label>
                <textarea
                  className="input w-full"
                  rows={4}
                  value={aboutCopy}
                  onChange={(e) => setAboutCopy(e.target.value)}
                  placeholder="Tell your story - who you are, what you do, why you do it..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  What should we highlight?
                </label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={callouts}
                  onChange={(e) => setCallouts(e.target.value)}
                  placeholder="e.g., Bestselling products, special collections, collaborations, achievements..."
                />
              </div>
            </div>
          </div>

          {/* Pages */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Must-Have Pages</h2>
            <div className="flex flex-wrap gap-3">
              {MUST_HAVE_PAGES.map((page) => (
                <label key={page.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mustHavePages.includes(page.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setMustHavePages([...mustHavePages, page.key]);
                      } else {
                        setMustHavePages(mustHavePages.filter(p => p !== page.key));
                      }
                    }}
                    className="w-4 h-4 rounded border-ink-600 text-accent-500 focus:ring-accent-500"
                  />
                  <span className="text-sm text-ink-300">{page.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Assets Upload */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Upload Assets</h2>
            <p className="text-sm text-ink-400 mb-4">
              Upload your logo, brand photos, product photos, or brand kit files.
            </p>

            <div className="flex gap-3 mb-4">
              <select
                className="input"
                value={selectedAssetType}
                onChange={(e) => setSelectedAssetType(e.target.value as SiteAssetType)}
              >
                {Object.entries(ASSET_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.zip"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!uploadingType}
                className="btn-ghost"
              >
                {uploadingType ? (
                  <>
                    <div className="w-4 h-4 border-2 border-accent-500/30 border-t-accent-500 rounded-full animate-spin mr-2" />
                    Uploading {uploadProgress}%
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload File
                  </>
                )}
              </button>
            </div>

            {/* Uploaded assets list */}
            {assets.length > 0 && (
              <div className="border-t border-ink-800 pt-4 mt-4">
                <h4 className="text-sm font-medium text-ink-300 mb-3">Uploaded Files</h4>
                <div className="space-y-2">
                  {assets.map((asset) => (
                    <div key={asset.id} className="flex items-center gap-3 p-2 bg-ink-800/50 rounded">
                      <div className="w-10 h-10 bg-ink-700 rounded flex items-center justify-center overflow-hidden">
                        {asset.content_type.startsWith('image/') ? (
                          <img src={asset.public_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <svg className="w-5 h-5 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-ink-200 truncate">{asset.file_name}</p>
                        <p className="text-xs text-ink-500">
                          {ASSET_TYPE_LABELS[asset.asset_type]} • {formatFileSize(asset.size_bytes)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteAsset(asset.id)}
                        className="btn-ghost p-1 text-ink-400 hover:text-accent-400"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="card p-6">
            <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Additional Notes</h2>
            <textarea
              className="input w-full"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else you'd like us to know..."
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? 'Submitting...' : 'Submit Intake Form'}
            </button>
          </div>
        </form>
      </div>
  );
}
